package com.dynamicruntime.webapp

import com.dynamicruntime.common.home.HMENU
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h1
import react.dom.html.ReactHTML.li
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.ul
import react.useEffect
import react.useRef
import react.useState
import web.cssom.ClassName

/** Coroutine scope for the documents page's suspend fetches. */
private val docsScope = MainScope()

/**
 * The documents page (issue #554): `/wa/docs` lists the served documents, and `/wa/docs/<id>` shows
 * one, with "← Documents" back to the listing. A document is a destination of its own -- it used to be a
 * mode of Home, opened in place with `replaceRoute`, so browser Back skipped the listing altogether -- so
 * opening one is an ordinary link navigation, which is what gives it a history entry. A bare `#doc=<id>`
 * (an older bookmark or in-document link) routes here as well.
 *
 * Which document is open is read from the address on every render rather than held in state: the router's page
 * id is the same for the listing and a document, and a navigation bumps the refresh generation, which is
 * what re-renders this page.
 */
val DocsPage = FC<Props> {
    var config by useState<HomeConfig?>(null)
    var copy by useState(Copy.empty)
    var docText by useState<String?>(null)
    // The document fetch has its own error, shown beside the (still-visible) heading, so a failed document does
    // not blank the whole page the way a failed config load does, and does not race the config's `error = null`.
    var docError by useState<DisplayError?>(null)
    var error by useState<DisplayError?>(null)
    val generation = useRefreshGeneration()
    // Monotonic token so an out-of-order document response is dropped rather than overwriting the open one.
    val latestDoc = useRef(0)
    val openId = routeParams()[HP.doc]
    // The heading a link into the document named (issue #1136), scrolled to once the document has rendered.
    val section = routeParams()[HP.section]

    // The same config Home reads (the links, and the fragment carrying the nav copy), re-read per generation.
    // And on every idle poll: it reads the home config, a polled endpoint, and static copy alone (issue #1372).
    val polls = useIdlePolls()
    useEffect(generation, polls) {
        docsScope.launch {
            try {
                val loaded = HomeApi.fetchConfig()
                config = loaded
                copy = fetchCopyWithRetry(loaded.fragment) {
                    apiResult { HomeApi.fetchConfig().fragment }.valueOrNull()
                }
                error = null
            } catch (e: Throwable) {
                error = userFacingError(e)
            }
        }
    }

    // Fetch whichever document the address names, keyed on the document and its build -- never on the config object,
    // which a refresh replaces whether or not anything changed (issue #1313): re-running then cleared the text, the
    // page collapsed, and the reader was scrolled to the top. Clear the old text and error only when the document
    // changes, so switching documents never shows the previous body under the new heading, while a new build of the
    // open one keeps its text until the replacement arrives. A token drops a slow response that a later switch has
    // superseded (docsScope is module-global, so a launch outlives this effect).
    val openLink = config?.links?.firstOrNull { it.id == openId }
    val lastOpen = useRef<String>(null)
    useEffect(openId, openLink?.docId, openLink?.buildId) {
        val link = openLink
        if (lastOpen.current != openId) {
            lastOpen.current = openId
            docText = null
            docError = null
        }
        if (link != null) {
            val token = (latestDoc.current ?: 0) + 1
            latestDoc.current = token
            docsScope.launch {
                try {
                    val loaded = HomeApi.fetchDoc(link.docId, link.buildId)
                    if (latestDoc.current == token) docText = loaded
                } catch (e: Throwable) {
                    if (latestDoc.current == token) docError = userFacingError(e)
                }
            }
        }
    }

    // After the document's text is in the page, so the heading it names exists. Again when the section changes on a
    // document already open -- a link from one of its headings to another.
    useEffect(docText, section) {
        if (docText != null && section != null) {
            document.getElementById(section)?.asDynamic()?.scrollIntoView(js("({ block: 'start' })"))
        }
    }

    div {
        className = ClassName("card wide")
        val current = config
        val links = current?.links ?: emptyList()
        val doc = links.firstOrNull { it.id == openId }
        when {
            error != null -> errorText("Couldn't load the documents.", error!!)
            current == null -> p { className = ClassName("subtitle"); +"Loading…" }
            doc != null -> {
                backToListing(HMENU.pageDocs)
                // A document's own first heading is its title; the label stands in only where it has none (issue #1238).
                if (docText?.let { docHasOwnTitle(it) } != true) h1 { +doc.label }
                docError?.let { errorText("Couldn't load this document.", it) }
                docText?.let { text ->
                    Markdown {
                        source = text
                        // Interior repo-relative links become in-app document links or source-repo links as
                        // the document renders (issue #492).
                        // An image kept in the repository that a component ships draws as that image (issue #1463).
                        linkResolver = docLinkResolver(
                            doc.sourcePath, links, current.sourceRepoBase,
                            componentImageAddressByPath(current.imagePaths, current.images),
                        )
                    }
                }
            }
            else -> {
                h1 { +copy.t("nav", "title", "Documents") }
                // A suite's inner pages are reached from its entry page, not listed (issue #1136).
                val listed = links.filter { it.listed }
                if (listed.isEmpty()) {
                    p { className = ClassName("type-hint"); +copy.t("nav", "emptyNote", "") }
                } else {
                    // A list of links with a line on each, the shape the Operator overview and the Debug index
                    // use for a listing -- not Home's toolbar of link buttons. The description is served with
                    // the link, so a new document arrives with its own.
                    ul {
                        className = ClassName("index-list")
                        for (link in listed) li {
                            a { href = docHref(link.id); +link.label }
                            if (link.description.isNotEmpty()) +" \u2014 ${link.description}"
                        }
                    }
                }
            }
        }
    }
}

/** One link per document, to the document's own page. Home's link presentations use it too. */
fun ChildrenBuilder.docLinks(links: List<HomeLink>) {
    links.forEach { link ->
        a {
            className = ClassName("link-button")
            href = docHref(link.id)
            +link.label
        }
    }
}

/**
 * The href of a document's page, and of a [section] within it (issue #1136) -- a heading's id. One builder, so Home's
 * links, the listing, and the link resolver agree.
 */
fun docHref(id: String, section: String? = null): String =
    routeHref(listOfNotNull(HP.page to HMENU.pageDocs, HP.doc to id, section?.let { HP.section to it }))

/**
 * Whether [text]'s first heading is a level-1 heading, which the page then shows as the document's title rather than
 * the listing's label (issue #1238): true for the Markdown documents, whose first heading matches their label, even
 * where a navigation line or an HTML comment comes before it, as the demo's pages have; false for the plain-text
 * license, which has no heading, and for a document that opens at a deeper level. Pure, covered under `jsNodeTest`.
 */
fun docHasOwnTitle(text: String): Boolean {
    var inComment = false
    var inFence = false
    for (raw in text.lineSequence()) {
        val line = raw.trim()
        if (inComment) {
            if (line.endsWith("-->")) inComment = false
            continue
        }
        if (line.startsWith("```")) {
            inFence = !inFence
            continue
        }
        if (inFence) continue
        if (line.startsWith("<!--")) {
            inComment = !line.endsWith("-->")
            continue
        }
        if (line.startsWith("#")) return line.startsWith("# ")
    }
    return false
}
